// Reusable UI primitives
import { useState, useEffect } from 'react'

export function Spinner({ size = 'md', className = '' }) {
  const sizes = { sm: 'h-4 w-4', md: 'h-8 w-8', lg: 'h-12 w-12' }
  return (
    <div className={`inline-block ${sizes[size]} ${className}`}>
      <div className="w-full h-full rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
    </div>
  )
}

export function PageSpinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <Spinner size="lg" />
    </div>
  )
}

export function ErrorMessage({ message, retry }) {
  return (
    <div className="text-center py-12">
      <div className="text-4xl mb-3">⚠️</div>
      <p className="text-gray-600 dark:text-gray-300 mb-4">{message || 'Something went wrong.'}</p>
      {retry && (
        <button onClick={retry} className="btn-outline text-sm">Try Again</button>
      )}
    </div>
  )
}

export function Badge({ children, variant = 'primary' }) {
  const variants = {
    primary: 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-red-400',
    gold: 'bg-gold/20 text-yellow-700 dark:text-gold',
    green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold font-display ${variants[variant]}`}>
      {children}
    </span>
  )
}

export function Divider({ label }) {
  if (!label) return <hr className="border-gray-200 dark:border-gray-700 my-4" />
  return (
    <div className="flex items-center gap-4 my-6">
      <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
      <span className="text-sm text-gray-400 font-display">{label}</span>
      <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
    </div>
  )
}

export function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-end justify-between mb-6">
      <div>
        <h2 className="section-title">{title}</h2>
        {subtitle && <p className="section-subtitle text-sm">{subtitle}</p>}
      </div>
      {action}
    </div>
  )
}

export function QuantityControl({ quantity, onChange, min = 1, max = 999 }) {
  const [draft, setDraft] = useState(String(quantity))

  // Keep draft in sync when quantity changes externally (e.g. server sync)
  useEffect(() => { setDraft(String(quantity)) }, [quantity])

  const commit = () => {
    const n = parseInt(draft, 10)
    if (!isNaN(n) && n >= min) onChange(Math.min(max, Math.max(min, n)))
    else setDraft(String(quantity)) // revert invalid
  }

  return (
    <div className="flex items-center border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
      <button
        onClick={() => onChange(Math.max(min, quantity - 1))}
        className="px-3 py-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-bold text-lg leading-none"
      >
        −
      </button>
      <input
        type="number"
        min={min}
        max={max}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter')  { commit(); e.target.blur() }
          if (e.key === 'Escape') { setDraft(String(quantity)); e.target.blur() }
        }}
        onClick={e => e.target.select()}
        className="w-10 text-center text-sm font-display font-semibold text-gray-800 dark:text-white bg-transparent focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700 py-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <button
        onClick={() => onChange(Math.min(max, quantity + 1))}
        className="px-3 py-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-bold text-lg leading-none"
      >
        +
      </button>
    </div>
  )
}

export function EmptyState({ icon = '📦', title, description, action }) {
  return (
    <div className="text-center py-16 px-4">
      <div className="text-6xl mb-4">{icon}</div>
      <h3 className="font-heading text-xl text-gray-800 dark:text-white mb-2">{title}</h3>
      {description && <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">{description}</p>}
      {action}
    </div>
  )
}

export function Breadcrumbs({ items }) {
  return (
    <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-6">
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <span className="text-gray-300 dark:text-gray-600">/</span>}
          {item.href ? (
            <a href={item.href} className="hover:text-primary transition-colors">{item.label}</a>
          ) : (
            <span className="text-gray-800 dark:text-white font-medium">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  )
}

// ── Product Card Skeleton ────────────────────────────────────────
export function ProductCardSkeleton() {
  return (
    <div className="card overflow-hidden animate-pulse">
      <div className="aspect-square bg-gray-200 dark:bg-gray-700" />
      <div className="p-3 space-y-2">
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mt-3" />
      </div>
    </div>
  )
}

// ── Section Skeleton (horizontal row of cards) ───────────────────
export function SectionSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array(count).fill(0).map((_, i) => <ProductCardSkeleton key={i} />)}
    </div>
  )
}
